import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  collection,
  addDoc,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { Send, Plus, MessageCircle, BookOpen, Loader2,ChevronDown } from 'lucide-react';

import { db, auth } from '../firebase';
import BackHomeButton from '../components/BackHomeButton';
import Get_Help from '../components/Get_Help';
import { trackClick } from './trackClick';

import { functions } from '../firebase'; // Ensure path is correct
import { httpsCallable } from 'firebase/functions';

const CHATS_COLLECTION = 'chapter_discussion_chats';
const NO_LESSON = '__NO_LESSON__';
const DEFAULT_TEACHER_MODE = 'adaptive';

const createInitialNonChapterProfile = () => ({
  interest: '',
  topic: '',
  stage: 'ask_interest',
});

const normalizeNonChapterProfile = profile => ({
  interest: profile?.interest || '',
  topic: profile?.topic || '',
  stage: profile?.stage || (profile?.topic ? 'ready' : profile?.interest ? 'ask_topic' : 'ask_interest'),
});

const inferNonChapterProfile = messages => {
  const userMessages = (messages || []).filter(message => message.role === 'user');
  const interest = userMessages[0]?.text?.slice(0, 200) || '';
  const topic = userMessages[1]?.text?.slice(0, 120) || '';

  return {
    interest,
    topic,
    stage: topic ? 'ready' : interest ? 'ask_topic' : 'ask_interest',
  };
};

const getStandardNumber = value => {
  const match = String(value || '').match(/\d+/);
  return match ? Number(match[0]) : null;
};

const countUserMessages = conversation =>
  (conversation || []).filter(message => message.role === 'user').length;

const getStandardDifficultyGuide = standardValue => {
  const standardNumber = getStandardNumber(standardValue);

  if (!standardNumber) {
    return 'Use age-appropriate school-level questions and avoid nursery-level recall unless the student is clearly struggling.';
  }

  if (standardNumber <= 2) {
    return 'Use concrete examples, simple reasoning, and short one-step questions, but keep them at the child standard.';
  }

  if (standardNumber <= 5) {
    return 'Use standard-level vocabulary, 2-step reasoning, word problems, and short explanation questions.';
  }

  if (standardNumber <= 8) {
    return 'Use multi-step reasoning, application, comparison, cause-effect, data interpretation, and short justification.';
  }

  return 'Use analytical, application-based, and exam-style questions that require reasoning and justification.';
};

const getPracticeDepthGuide = conversation => {
  const userMessageCount = countUserMessages(conversation);

  if (userMessageCount <= 2) {
    return 'Start directly at the student standard, with one warm-up only if needed.';
  }

  if (userMessageCount <= 5) {
    return 'Increase one notch beyond the warm-up level and require reasoning, not only recall.';
  }

  return 'Use challenging standard-level questions and vary the skill tested each turn.';
};

const getRecentTutorQuestions = conversation => {
  const questionLines = (conversation || [])
    .filter(message => message.role === 'assistant' && message.text)
    .flatMap(message => String(message.text).split('\n'))
    .map(line => line.trim())
    .filter(line => line.endsWith('?') || /^question\s*:/i.test(line));

  const recentQuestions = questionLines
    .slice(-6)
    .map((line, index) => `${index + 1}. ${line.replace(/^question\s*:/i, '').trim()}`);

  return recentQuestions.length > 0
    ? recentQuestions.join('\n')
    : 'No recent tutor questions yet.';
};

function ChapterChat() {
  const location = useLocation();
  const messagesEndRef = useRef(null);

  const [user, setUser] = useState(null);
  const [standard, setStandard] = useState('unspecified');
  const [school, setSchool] = useState('unspecified');

  const [subject, setSubject] = useState(location.state?.subject || '');
  const [savedLessons, setSavedLessons] = useState([]);
  const [selectedLessonId, setSelectedLessonId] = useState(
    location.state?.selectedLessonId || ''
  );
  const [selectedLessonName, setSelectedLessonName] = useState('');
  const [selectedLessonText, setSelectedLessonText] = useState('');

  const [chatSessions, setChatSessions] = useState([]);
  const [activeChatId, setActiveChatId] = useState('');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');

  const [loadingChats, setLoadingChats] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState('');
  const [teacherMode, setTeacherMode] = useState(DEFAULT_TEACHER_MODE);
  const [showPastChats, setShowPastChats] = useState(false);
  const navigate = useNavigate();
  const WORKSHEET_ROUTE = '/Eascode_Agent_Lesson';
  const QUIZ_ROUTE = '/AIQuestionTest';
  const [currentTopicMemory, setCurrentTopicMemory] = useState(null);

  const NON_CHAPTER_SUBJECTS = ['maths', 'math', 'mathematics', 'english language', 'hindi language'];

const LANGUAGE_SUBJECTS = [
  'french',
  'german',
  'spanish',
  'sanskrit',
  'urdu',
  'punjabi',
  'marathi',
  'bengali',
  'tamil',
  'telugu',
  'kannada',
  'malayalam',
  'gujarati',
];

const isNonChapterSubject = subjectName => {
  const name = (subjectName || '').trim().toLowerCase();

  return (
    NON_CHAPTER_SUBJECTS.includes(name) ||
    LANGUAGE_SUBJECTS.includes(name) ||
    name.endsWith(' language')
  );
};

const [nonChapterProfile, setNonChapterProfile] = useState(createInitialNonChapterProfile);

const resetNonChapterProfile = () => {
  setNonChapterProfile(createInitialNonChapterProfile());
};

const buildWelcomePrompt = (lessonText, lessonName) => `
You are a courteous tutor for a student of standard ${standard}.

Subject: ${subject}
Lesson or chapter: ${lessonName || selectedLessonName || 'Selected chapter'}

Full lesson text:
${lessonText || 'No lesson text was selected.'}

Instructions:
- Read the full lesson text.
- Give a short, clear summary of this chapter.
- Then list all major topics covered in the chapter.
- Keep topic names short and student-friendly.
- Number the topics.
- Do not hallucinate. Only use the lesson text.
- Do not use foul or inappropriate language.
- Stay strictly within this subject and chapter.
- End with this exact question: What would you like to learn about this chapter?

Required format:
Chapter Summary:
<short summary>

Topics in this Chapter:
1. <topic>
2. <topic>
3. <topic>

What would you like to learn about this chapter?
`.trim();

  const subjectList = useMemo(() => {
    const coreSubjects = ['English Language', 'Hindi Language', 'Maths'];
    return [...new Set([...savedLessons.map(l => l.subject), ...coreSubjects])]
      .filter(Boolean)
      .sort();
  }, [savedLessons]);

  const lessonsForSubject = useMemo(() => {
    return savedLessons
      .filter(lesson => lesson.subject === subject)
      .sort((a, b) => (a.lessonName || '').localeCompare(b.lessonName || ''));
  }, [savedLessons, subject]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async currentUser => {
      if (!currentUser) return;

      setUser(currentUser);

      const profileSnap = await getDoc(doc(db, 'profiles', currentUser.uid));
      if (profileSnap.exists()) {
        const profile = profileSnap.data();
        setStandard(profile.standard || 'unspecified');
        setSchool(profile.school || 'unspecified');
      }

      await fetchChatSessions(currentUser);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchSavedLessons = async () => {
      if (!user || !school || !standard) return;

      const coreSubjects = ['English Language', 'Hindi Language', 'Maths'];
      const snapshot = await getDocs(collection(db, 'Saved_lessons'));

      const lessons = snapshot.docs
        .map(docSnap => ({ id: docSnap.id, ...docSnap.data() }))
        .filter(item =>
          (item.email === user.email || item.sharedWith === 'parents') &&
          item.school === school &&
          (coreSubjects.includes(item.subject) || item.standard === standard)
        );

      setSavedLessons(lessons);
    };

    fetchSavedLessons();
  }, [user, school, standard]);

  useEffect(() => {
    if (!selectedLessonId || selectedLessonId === NO_LESSON) {
      setSelectedLessonName('');
      setSelectedLessonText('');
      return;
    }

    const selected = savedLessons.find(lesson => lesson.id === selectedLessonId);
    if (selected) {
      setSelectedLessonName(selected.lessonName || '');
      setSelectedLessonText(selected.text || '');
    }
  }, [selectedLessonId, savedLessons]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  const fetchChatSessions = async currentUser => {
    if (!currentUser) return;

    setLoadingChats(true);

    try {
      const q = query(
        collection(db, CHATS_COLLECTION),
        where('uid', '==', currentUser.uid)
      );

      const snapshot = await getDocs(q);

      const sessions = snapshot.docs
        .map(docSnap => ({ id: docSnap.id, ...docSnap.data() }))
        .sort((a, b) => getTime(b.updatedAt) - getTime(a.updatedAt));

      setChatSessions(sessions);
    } catch (err) {
      console.error('Error fetching chats:', err);
    } finally {
      setLoadingChats(false);
    }
  };

  const getTime = value => {
    if (!value) return 0;
    if (value.toDate) return value.toDate().getTime();
    return new Date(value).getTime();
  };

  const resolveLessonText = async lessonId => {
    if (!lessonId || lessonId === NO_LESSON) return '';

    const localLesson = savedLessons.find(lesson => lesson.id === lessonId);
    if (localLesson) return localLesson.text || '';

    const lessonSnap = await getDoc(doc(db, 'Saved_lessons', lessonId));
    return lessonSnap.exists() ? lessonSnap.data().text || '' : '';
  };

  const loadChat = async session => {
    setLoadingMessages(true);
    setCurrentTopicMemory(session.currentTopicMemory || null);
    setStatus('');

    try {
      setActiveChatId(session.id);
      setSubject(session.subject || '');
      setSelectedLessonId(session.lessonId || NO_LESSON);
      setSelectedLessonName(session.lessonName || '');
      setTeacherMode(session.teacherMode || DEFAULT_TEACHER_MODE);

      const lessonText = await resolveLessonText(session.lessonId);
      setSelectedLessonText(lessonText);

      const messagesRef = collection(db, CHATS_COLLECTION, session.id, 'messages');
      const q = query(messagesRef, orderBy('clientCreatedAt', 'asc'));
      const snapshot = await getDocs(q);

      const loadedMessages = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));

      setMessages(loadedMessages);

      if (isNonChapterSubject(session.subject)) {
        setCurrentTopicMemory(null);
        setNonChapterProfile(
          session.nonChapterProfile
            ? normalizeNonChapterProfile(session.nonChapterProfile)
            : inferNonChapterProfile(loadedMessages)
        );
      } else {
        resetNonChapterProfile();
      }
    setShowPastChats(false);
    } catch (err) {
      console.error('Error loading chat:', err);
      setStatus('Could not load this chat.');

    } finally {
      setLoadingMessages(false);
    }
  };

  const createNewChat = async () => {
    if (!user) {
      alert('Please login first.');
      return;
    }

const subjectIsNonChapter = isNonChapterSubject(subject);

if (!subject || (!subjectIsNonChapter && !selectedLessonId)) {
  alert('Please select subject and lesson first.');
  return;
}

    setCurrentTopicMemory(null);

const initialNonChapterProfile = createInitialNonChapterProfile();

if (subjectIsNonChapter) {
  setNonChapterProfile(initialNonChapterProfile);
}

const effectiveLessonId = subjectIsNonChapter ? NO_LESSON : selectedLessonId;

const selectedLesson = savedLessons.find(lesson => lesson.id === effectiveLessonId);

const lessonName = subjectIsNonChapter
  ? 'Practice without a chapter'
  : selectedLesson?.lessonName || selectedLessonName || 'Selected Lesson';

const lessonText = subjectIsNonChapter
  ? ''
  : selectedLesson?.text || selectedLessonText || '';

    setSelectedLessonName(lessonName);
    setSelectedLessonText(lessonText);

    const chatRef = await addDoc(collection(db, CHATS_COLLECTION), {
      uid: user.uid,
      email: user.email,
      subject,
      lessonId: effectiveLessonId,
      learningMode: subjectIsNonChapter ? 'non_chapter' : 'chapter',
      lessonName,
      teacherMode,
      nonChapterProfile: subjectIsNonChapter ? initialNonChapterProfile : null,
      currentTopicMemory: null,
      standard,
      school,
      title: `${subject} - ${lessonName}`,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      lastMessage: 'New chat started',
      messageCount: 0,
    });



    setActiveChatId(chatRef.id);
    setMessages([]);
    setInput('');
setStatus('Preparing chapter summary...');



let assistantMessage;

if (subjectIsNonChapter) {
  assistantMessage = createMessage('assistant', buildNonChapterWelcomeText());
} else {
  const welcomeText = await fetchAIResponse(buildWelcomePrompt(lessonText, lessonName));

  assistantMessage = createMessage(
    'assistant',
    welcomeText ||
      'This chapter is ready for discussion.\n\nWhat would you like to learn about this chapter?'
  );
}

await addDoc(collection(db, CHATS_COLLECTION, chatRef.id, 'messages'), {
  ...assistantMessage,
  createdAt: serverTimestamp(),
});

setMessages([assistantMessage]);

await updateDoc(doc(db, CHATS_COLLECTION, chatRef.id), {
  lastMessage: assistantMessage.text.slice(0, 180),
  messageCount: 1,
  updatedAt: serverTimestamp(),
});

setStatus('');
await fetchChatSessions(user);
  };

  const resetCurrentChat = () => {
    setCurrentTopicMemory(null);
    setActiveChatId('');
    setMessages([]);
    setInput('');
    setStatus('Click New Chat to start discussing this chapter.');
  };

  const fetchAIResponse = async prompt => {

    const callGemini = httpsCallable(functions, 'callGemini');

  // 2. Try Gemini as fallback
  try {
    const result = await callGemini({
      contents: [{ role: "user", parts: [{ text: prompt }] }],
      generationConfig: {
        maxOutputTokens: 8192,
        temperature: 0.7
      }
    });

    // 🛡️ Secure Note: The App Check token was sent automatically!
    // result.data contains the body of the response from your backend
    return result.data.candidates?.[0]?.content?.parts?.[0]?.text;

  } catch (err) {
    console.error('Gemini error:', err.message);
    
    // Check for specific security failures
    if (err.code === 'failed-precondition') {
      alert("Security Check Failed: Please refresh the page.");
    }
  }

  };

  const safeJsonParse = text => {
  try {
    const cleaned = (text || '')
      .replace(/```json/gi, '')
      .replace(/```/g, '')
      .trim();

    const firstBrace = cleaned.indexOf('{');
    const lastBrace = cleaned.lastIndexOf('}');

    if (firstBrace === -1 || lastBrace === -1) return null;

    return JSON.parse(cleaned.slice(firstBrace, lastBrace + 1));
  } catch (err) {
    console.error('Topic memory JSON parse failed:', err);
    return null;
  }
};

const buildNonChapterPrompt = (conversation, profile) => {
  const chatHistory = conversation
    .slice(-14)
    .map(msg => `${msg.role === 'assistant' ? 'Tutor' : 'Student'}: ${msg.text}`)
    .join('\n');

  const standardGuide = getStandardDifficultyGuide(standard);
  const progressGuide = getPracticeDepthGuide(conversation);
  const recentTutorQuestions = getRecentTutorQuestions(conversation);
  const teacherStyle =
    teacherMode === 'strict'
      ? 'Strict teacher'
      : teacherMode === 'lenient'
        ? 'Lenient teacher'
        : 'Adaptive standard-level teacher';

  const teacherInstructions =
    teacherMode === 'strict'
      ? `
- Ask tougher application-based questions.
- Evaluate answers strictly.
- Mark weak, vague, or incomplete answers clearly.
`
      : teacherMode === 'lenient'
        ? `
- Keep the tone gentle, but keep the question at the student's standard.
- Use simpler wording only when the student is struggling.
- Accept partially correct answers when the main idea is present.
`
      : `
- Adapt the next question based on the student's last answer.
- If the student is correct, raise difficulty one notch.
- If the student is incorrect, keep the same concept but change the numbers, wording, and answer.
- Prefer reasoning and application over basic recall.
`;

  return `
You are a courteous tutor for a student of standard ${standard}.

Subject: ${subject}
Teaching style: ${teacherStyle}
Student interest: ${profile.interest}
Practice topic: ${profile.topic}

Standard difficulty guide:
${standardGuide}

Current practice depth:
${progressGuide}

Recent tutor questions to avoid:
${recentTutorQuestions}

Conversation so far:
${chatHistory}

Instructions:
- Stay strictly within ${subject}.
- Do not use foul or inappropriate language.
- Do not hallucinate.
- Use the student's interest only for school-friendly examples.
- If the interest is inappropriate, ignore it and use neutral school-friendly examples.
- If the student asks outside ${subject}, politely decline and bring them back to ${subject}.
- If the student gives an answer, evaluate it as Correct, Partly Correct, or Incorrect.
- Then ask exactly one next question.
- Do not repeat the same question pattern, story context, numbers, or final correct answer used recently.
- Before asking, silently verify that the new question's correct answer is different from recent correct answers in the conversation.
- Ask at the student's standard, not below it.
- For Maths, create word problems around the student's interest.
- For Maths, vary the operation or skill each turn. Prefer multi-step word problems for standard 3 and above.
- For language subjects, create grammar, vocabulary, sentence, reading, or writing practice around the student's interest.
${teacherInstructions}
`.trim();
};

const buildNonChapterWelcomeText = () => `
Great. We can practice ${subject} without using a chapter.

First, tell me one school-friendly thing you are interested in, such as a sport, game, music, food, place, or public figure.

After that, I will ask which ${subject} topic you want to practice and create questions around your interest.
`.trim();

const buildTopicExtractionPrompt = (studentMessage, conversation) => `
You are helping a chapter tutor identify the exact topic being discussed.

Return JSON only.

JSON format:
{
  "isRelatedToChapter": true,
  "topicName": "topic name",
  "topicSummary": "short summary of the topic",
  "topicContext": "complete relevant section or sections from the lesson text",
  "declineReason": ""
}

Subject: ${subject}
Lesson or chapter: ${selectedLessonName || 'Selected chapter'}

Recent conversation:
${conversation.slice(-8).map(msg => `${msg.role}: ${msg.text}`).join('\n')}

Latest student message:
${studentMessage}

Full lesson text:
${selectedLessonText || 'No lesson text selected.'}

Rules:
- If the student message is unrelated to this subject and chapter, set isRelatedToChapter to false.
- If the message is random, spam, abusive, or meaningless, set isRelatedToChapter to false.
- If related, identify the topic and extract the full relevant topic section from the lesson text.
- topicContext must contain the actual lesson content needed to answer, not just a summary.
- Do not hallucinate.
- Use only the lesson text.
`.trim();

const buildTopicContinuityPrompt = (studentMessage, conversation) => `
Decide whether the student is still discussing the current topic.

Return JSON only.

JSON format:
{
  "sameTopic": true,
  "shouldExtractNewTopic": false,
  "isGarbageOrUnrelated": false,
  "reason": "short reason"
}

Current topic:
${currentTopicMemory?.topicName || 'No topic yet'}

Current topic summary:
${currentTopicMemory?.topicSummary || ''}

Recent conversation:
${conversation.slice(-8).map(msg => `${msg.role}: ${msg.text}`).join('\n')}

Latest student message:
${studentMessage}

Rules:
- If the student is answering the tutor's previous question about the current topic, sameTopic should be true.
- If the student asks about a different topic from the same chapter, sameTopic should be false and shouldExtractNewTopic should be true.
- If the input is garbage, spam, abusive, or unrelated to the chapter, isGarbageOrUnrelated should be true and shouldExtractNewTopic should be false.
- Do not hallucinate.
`.trim();

const extractTopicMemoryFromLesson = async (studentMessage, conversation) => {
  if (!selectedLessonText) {
    return {
      isRelatedToChapter: false,
      topicName: '',
      topicSummary: '',
      topicContext: '',
      declineReason: 'No lesson text is available for this chat.',
    };
  }

  const response = await fetchAIResponse(
    buildTopicExtractionPrompt(studentMessage, conversation)
  );

  const parsed = safeJsonParse(response);

  const memory = parsed || {
    isRelatedToChapter: true,
    topicName: 'Full chapter context',
    topicSummary: 'Using the full lesson because a focused topic could not be extracted.',
    topicContext: selectedLessonText,
    declineReason: '',
  };

  if (memory.isRelatedToChapter !== false && memory.topicContext) {
    setCurrentTopicMemory(memory);
    persistTopicMemory(memory);
  }

  return memory;
};

const checkTopicContinuity = async (studentMessage, conversation) => {
  if (!currentTopicMemory) {
    return {
      sameTopic: false,
      shouldExtractNewTopic: true,
      isGarbageOrUnrelated: false,
    };
  }

  const response = await fetchAIResponse(
    buildTopicContinuityPrompt(studentMessage, conversation)
  );

  const parsed = safeJsonParse(response);

  if (!parsed) {
    return {
      sameTopic: true,
      shouldExtractNewTopic: false,
      isGarbageOrUnrelated: false,
    };
  }

  return parsed;
};

const getTopicMemoryForMessage = async (studentMessage, conversation) => {
  if (!currentTopicMemory) {
    return await extractTopicMemoryFromLesson(studentMessage, conversation);
  }

  const continuity = await checkTopicContinuity(studentMessage, conversation);

  if (continuity.isGarbageOrUnrelated) {
    return currentTopicMemory;
  }

  if (continuity.sameTopic || !continuity.shouldExtractNewTopic) {
    return currentTopicMemory;
  }

  return await extractTopicMemoryFromLesson(studentMessage, conversation);
};

  const buildPrompt = (conversation, topicMemory) => {

    const chatHistory = conversation
      .slice(-14)
      .map(msg => `${msg.role === 'assistant' ? 'Tutor' : 'Student'}: ${msg.text}`)
      .join('\n');

    const standardGuide = getStandardDifficultyGuide(standard);
    const progressGuide = getPracticeDepthGuide(conversation);
    const recentTutorQuestions = getRecentTutorQuestions(conversation);
    const teacherStyle =
    teacherMode === 'strict'
      ? 'Strict teacher'
      : teacherMode === 'lenient'
        ? 'Lenient teacher'
        : 'Adaptive standard-level teacher';

    const teacherInstructions =
    teacherMode === 'strict'
        ? `
    - Use a strict teacher style while still being polite.
    - Ask tougher application-based questions from the selected chapter.
    - Prefer questions that check reasoning, inference, cause-effect, comparison, and real understanding.
    - When evaluating the student's answer, be stricter. Mark weak, vague, copied, or incomplete understanding clearly.
    - If the student is partly correct, say what is correct and what is missing.
    `
        : teacherMode === 'lenient'
          ? `
    - Use a gentle teacher style while still keeping questions at the student's standard.
    - Use simpler wording only when the student is struggling.
    - Accept partially correct answers when the main idea is present.
    - If the student is wrong, explain gently and give a correction.
    `
        : `
    - Use an adaptive teacher style.
    - If the student is correct, raise difficulty one notch.
    - If the student is partly correct or incorrect, explain briefly and ask a different standard-level question on the same concept.
    - Prefer reasoning, application, comparison, cause-effect, and short justification over basic recall.
    `;

    return `
You are a friendly but focused tutor for a student of standard ${standard}.

Subject: ${subject}
Lesson or chapter: ${selectedLessonName || 'Not selected'}
Teaching style: ${teacherStyle}
School: ${school}

Current topic:
${topicMemory?.topicName || 'No focused topic identified'}

Current topic summary:
${topicMemory?.topicSummary || 'No topic summary available.'}

Relevant lesson text for current topic:
${topicMemory?.topicContext || 'No relevant lesson text found.'}

Standard difficulty guide:
${standardGuide}

Current practice depth:
${progressGuide}

Recent tutor questions to avoid:
${recentTutorQuestions}

Topic status:
${topicMemory?.isRelatedToChapter === false ? `The latest input may be unrelated. Reason: ${topicMemory?.declineReason || 'Not related to the selected chapter.'}` : 'The latest input is being handled within the selected chapter.'}

Conversation so far:
${chatHistory}

Instructions:
- Be courteous, patient, and encouraging at all times.
- Never use foul, abusive, insulting, adult, violent, or inappropriate language.
- Do not hallucinate. Use the selected lesson text as the main source of truth.
- Only answer questions related to the selected subject and lesson or chapter.
- If the student provides both a question and their own answer, evaluate whether their answer is correct using only the selected lesson text.
- Clearly say Correct, Partly Correct, or Incorrect.
- Explain the reason briefly.
- If incorrect or partly correct, provide the corrected answer using only the selected lesson text.
- Then ask one short follow-up question from the same chapter.
- If the student asks something outside the selected subject or chapter, politely decline and ask them to ask a question from this chapter.
- If the input is random, meaningless, abusive, spam, or garbage text, ignore the bad content and politely guide the student back to the selected chapter.
- If the answer is not present in the lesson text, say that clearly. Do not invent facts.
- Keep the answer clear for standard ${standard}.
- Ask at the student's standard, not below it.
- Do not repeat the same question pattern, story context, wording, or final correct answer used recently.
- Before asking the next question, silently verify that it tests a different angle or deeper level than the recent tutor questions.
- Be conversational and context-aware.
- After answering a valid chapter-related question, ask exactly one short question to check whether the student understood.
- If the student is answering your previous question, first tell them if they understood correctly, then continue.
- Reply in the same language the student is using when possible, as long as the reply remains appropriate and chapter-focused.
${teacherInstructions}
`.trim();
  };

  const getUserMessageCount = chatMessages => {
  return countUserMessages(chatMessages);
};

const hasCheckpointForCount = (chatMessages, userMessageCount) => {
  return chatMessages.some(
    msg =>
      msg.checkpoint?.type === 'practice_prompt' &&
      msg.checkpoint?.userMessageCount === userMessageCount
  );
};

const createPracticeCheckpoint = userMessageCount => ({
  role: 'assistant',
  text: 'You have discussed this chapter for a while. Would you like to practice now?',
  checkpoint: {
    type: 'practice_prompt',
    userMessageCount,
    actions: [
      {
        label: 'Attempt a Worksheet',
        action: 'worksheet',
      },
      {
        label: 'Take a Quiz',
        action: 'quiz',
      },
    ],
  },
  clientCreatedAt: new Date().toISOString(),
});

const handlePracticeAction = action => {
  const route = action === 'worksheet' ? WORKSHEET_ROUTE : QUIZ_ROUTE;

  navigate(route, {
    state: {
      subject,
      selectedLessonId,
      lessonName: selectedLessonName,
      chatId: activeChatId,
    },
  });
};

  const createMessage = (role, text) => ({
    role,
    text: text.trim(),
    clientCreatedAt: new Date().toISOString(),
  });

  const persistNonChapterProfile = async profile => {
    if (!activeChatId) return;

    try {
      await updateDoc(doc(db, CHATS_COLLECTION, activeChatId), {
        nonChapterProfile: normalizeNonChapterProfile(profile),
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('Error saving non-chapter profile:', err);
    }
  };

  const persistTopicMemory = async memory => {
    if (!activeChatId || !memory) return;

    try {
      await updateDoc(doc(db, CHATS_COLLECTION, activeChatId), {
        currentTopicMemory: memory,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('Error saving topic memory:', err);
    }
  };

  const getNonChapterAssistantText = async (studentMessage, conversation) => {
  const profile = nonChapterProfile || {
    interest: '',
    topic: '',
    stage: 'ask_interest',
  };

  if (!profile.interest) {
    const updatedProfile = {
      interest: studentMessage.slice(0, 200),
      topic: '',
      stage: 'ask_topic',
    };

    setNonChapterProfile(updatedProfile);
    await persistNonChapterProfile(updatedProfile);

    return `Thanks. I will use school-friendly examples based on your interest.\n\nWhich topic in ${subject} would you like to work on?`;
  }

  if (!profile.topic) {
    const updatedProfile = {
      ...profile,
      topic: studentMessage.slice(0, 120),
      stage: 'ready',
    };

    setNonChapterProfile(updatedProfile);
    await persistNonChapterProfile(updatedProfile);

    return await fetchAIResponse(
      buildNonChapterPrompt(conversation, updatedProfile)
    );
  }

  return await fetchAIResponse(
    buildNonChapterPrompt(conversation, profile)
  );
};

  const handleSend = async () => {
    const trimmedInput = input.trim();

    if (!activeChatId) {
      alert('Please click New Chat first, or open a previous chat.');
      return;
    }

    if (!trimmedInput || sending) return;

    setSending(true);
    setStatus('');

    const userMessage = createMessage('user', trimmedInput);
    const nextMessages = [...messages, userMessage];

    setMessages(nextMessages);
    setInput('');

    try {
      await addDoc(collection(db, CHATS_COLLECTION, activeChatId, 'messages'), {
        ...userMessage,
        createdAt: serverTimestamp(),
      });

let aiText;

if (isNonChapterSubject(subject)) {
  setStatus('Preparing your personalized practice...');

  aiText = await getNonChapterAssistantText(trimmedInput, nextMessages);

  setStatus('');
} else {
  setStatus(currentTopicMemory ? 'Checking chapter topic...' : 'Finding the relevant chapter topic...');

  const topicMemoryForAnswer = await getTopicMemoryForMessage(trimmedInput, nextMessages);

  setStatus('');

  aiText = await fetchAIResponse(
    buildPrompt(nextMessages, topicMemoryForAnswer)
  );
}

      const assistantMessage = createMessage(
        'assistant',
        aiText || 'I could not get a response right now. Please try again in a minute.'
      );

      await addDoc(collection(db, CHATS_COLLECTION, activeChatId, 'messages'), {
        ...assistantMessage,
        createdAt: serverTimestamp(),
      });

let finalMessages = [...nextMessages, assistantMessage];
let lastMessageText = assistantMessage.text;

const userMessageCount = getUserMessageCount(finalMessages);

if (
  userMessageCount > 0 &&
  userMessageCount % 5 === 0 &&
  !hasCheckpointForCount(finalMessages, userMessageCount)
) {
  const checkpointMessage = createPracticeCheckpoint(userMessageCount);

  await addDoc(collection(db, CHATS_COLLECTION, activeChatId, 'messages'), {
    ...checkpointMessage,
    createdAt: serverTimestamp(),
  });

  finalMessages = [...finalMessages, checkpointMessage];
  lastMessageText = checkpointMessage.text;
}

setMessages(finalMessages);

await updateDoc(doc(db, CHATS_COLLECTION, activeChatId), {
  updatedAt: serverTimestamp(),
  lastMessage: lastMessageText.slice(0, 180),
  messageCount: finalMessages.length,
  teacherMode,
});

      await fetchChatSessions(user);
    } catch (err) {
      console.error('Error sending message:', err);
      setStatus('Something went wrong while saving or sending this message.');
    } finally {
      setSending(false);
    }
  };

const handleSubjectChange = e => {
  const nextSubject = e.target.value;

  setSubject(nextSubject);
  setSelectedLessonName('');
  setSelectedLessonText('');
  setCurrentTopicMemory(null);
  resetNonChapterProfile();

  if (isNonChapterSubject(nextSubject)) {
    setSelectedLessonId(NO_LESSON);
  } else {
    setSelectedLessonId('');
  }

  resetCurrentChat();
};

  const handleLessonChange = e => {
    setSelectedLessonId(e.target.value);
    resetCurrentChat();
  };

  const handleTeacherModeChange = async e => {
    const nextTeacherMode = e.target.value;
    setTeacherMode(nextTeacherMode);

    if (!activeChatId) return;

    try {
      await updateDoc(doc(db, CHATS_COLLECTION, activeChatId), {
        teacherMode: nextTeacherMode,
        updatedAt: serverTimestamp(),
      });
      await fetchChatSessions(user);
    } catch (err) {
      console.error('Error saving teacher mode:', err);
    }
  };

  const chatReady = Boolean(activeChatId);

  return (
    <div className="min-h-screen bg-slate-50 py-3 sm:py-6 px-2 sm:px-6 font-sans">
      <div className="max-w-7xl mx-auto bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">
        <div className="flex items-center justify-between gap-3 p-3 sm:p-5 border-b border-slate-100">
          <BackHomeButton backState={{ subject, selectedLessonId }} />

          <div className="text-center">
            <h1 className="text-base sm:text-2xl font-black text-slate-900">
              Discuss a Chapter
            </h1>
            <p className="text-[10px] sm:text-xs text-slate-500 font-medium">
              Standard {standard} | {school}
            </p>
          </div>

          <Get_Help />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] min-h-[calc(100vh-150px)]">
          <aside className="border-r border-slate-100 bg-slate-50/70 p-3 sm:p-4 space-y-4">
            <div className="bg-white border border-slate-100 rounded-xl p-3 space-y-3">
              <label className="block text-xs font-bold text-slate-600">
                Subject
              </label>
              <select
                value={subject}
                onChange={handleSubjectChange}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-indigo-500"
              >
                <option value="">Select Subject</option>
                {subjectList.map(subj => (
                  <option key={subj} value={subj}>{subj}</option>
                ))}
              </select>

{isNonChapterSubject(subject) ? (
  <div className="bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2 text-xs text-indigo-700 font-semibold">
    No chapter needed. This chat will first ask the child their interest, then the topic they want to practice.
  </div>
) : (
  <>
              <label className="block text-xs font-bold text-slate-600">
                Lesson
              </label>
              <select
                value={selectedLessonId}
                onChange={handleLessonChange}
                disabled={!subject}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-indigo-500 disabled:bg-slate-100"
              >
                <option value="">Select Lesson</option>
                {lessonsForSubject.map(lesson => (
                  <option key={lesson.id} value={lesson.id}>
                    {lesson.lessonName || 'No Title'}
                  </option>
                ))}
                <option value={NO_LESSON}>Use without a lesson</option>
              </select>

                </>
)}

              <button
                onClick={() => {
                  createNewChat();
                  trackClick("Chapter_Chat_Started");
                }}
                className="w-full inline-flex items-center justify-center gap-2 bg-slate-900 text-white rounded-lg px-3 py-2 text-sm font-bold hover:bg-indigo-600 transition"
              >
                <Plus size={16} />
                New Chat
              </button>
            </div>

<div className="bg-white border border-slate-100 rounded-xl p-3">
  <button
    type="button"
    onClick={() => setShowPastChats(prev => !prev)}
    className="w-full flex items-center justify-between gap-2 mb-3"
    aria-expanded={showPastChats}
  >
    <span className="flex items-center gap-2">
      <MessageCircle size={16} className="text-indigo-600" />
      <span className="text-sm font-bold text-slate-800">Past Chats</span>
    </span>

    <ChevronDown
      size={18}
      className={`text-slate-500 transition-transform lg:hidden ${
        showPastChats ? 'rotate-180' : ''
      }`}
    />
  </button>

  <div className={`${showPastChats ? 'block' : 'hidden'} lg:block`}>
    <div className="space-y-2 max-h-[360px] overflow-y-auto">
      {loadingChats && (
        <p className="text-xs text-slate-400">Loading chats...</p>
      )}

      {!loadingChats && chatSessions.length === 0 && (
        <p className="text-xs text-slate-400">No saved chats yet.</p>
      )}

      {chatSessions.map(session => (
        <button
          key={session.id}
          onClick={() => loadChat(session)}
          className={`w-full text-left p-2 rounded-lg border transition ${
            activeChatId === session.id
              ? 'border-indigo-300 bg-indigo-50'
              : 'border-slate-100 hover:bg-slate-50'
          }`}
        >
          <p className="text-xs font-bold text-slate-800 truncate">
            {session.title || session.subject || 'Chapter Discussion'}
          </p>
          <p className="text-[10px] text-slate-500 truncate">
            {session.lastMessage || session.lessonName || ''}
          </p>
        </button>
      ))}
    </div>
  </div>
</div>
          </aside>

          <main className="flex flex-col bg-white">
            <div className="flex-1 p-3 sm:p-6 overflow-y-auto">
              {loadingMessages ? (
                <div className="h-full flex items-center justify-center text-indigo-600">
                  <Loader2 className="animate-spin" size={24} />
                </div>
              ) : messages.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center text-slate-400 gap-3">
                  <BookOpen size={40} />
                  <p className="text-sm font-semibold">
                    {chatReady
                      ? 'Ask your first question.'
                      : 'Select a subject and lesson, then click New Chat.'}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((msg, index) => (
                    <div
                      key={msg.id || `${msg.role}-${index}`}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                          msg.role === 'user'
                            ? 'bg-indigo-600 text-white rounded-br-md'
                            : 'bg-slate-100 text-slate-800 rounded-bl-md'
                        }`}
                      >
                        {msg.text}

                          {msg.checkpoint?.type === 'practice_prompt' && (
                            <div className="mt-3 flex flex-col sm:flex-row gap-2">
                            {msg.checkpoint.actions?.map(action => (
                                <button
                                key={action.action}
                                onClick={() => handlePracticeAction(action.action)}
                                className="bg-white text-indigo-700 border border-indigo-200 px-3 py-2 rounded-lg text-xs font-bold hover:bg-indigo-50 transition"
                                >
                                {action.label}
                                </button>
                            ))}
                            </div>
                        )}

                      </div>
                    </div>
                  ))}

                  {sending && (
                    <div className="flex justify-start">
                      <div className="bg-slate-100 text-slate-500 rounded-2xl rounded-bl-md px-4 py-3 text-sm flex items-center gap-2">
                        <Loader2 className="animate-spin" size={16} />
                        Thinking...
                      </div>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            {status && (
              <p className="px-4 pb-2 text-xs font-semibold text-indigo-500">
                {status}
              </p>
            )}

            <div className="border-t border-slate-100 p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row gap-2">

                <select
                value={teacherMode}
                onChange={handleTeacherModeChange}
                disabled={!chatReady || sending}
                className="sm:w-44 border border-slate-200 rounded-xl px-3 py-2 text-sm font-semibold outline-none focus:border-indigo-500 disabled:bg-slate-100"
                >
                <option value="adaptive">Adaptive Teacher</option>
                <option value="lenient">Lenient Teacher</option>
                <option value="strict">Strict Teacher</option>
                </select>

                <textarea
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  disabled={!chatReady || sending}
                  rows={2}
                  className="flex-1 border border-slate-200 rounded-xl px-3 py-2 text-sm resize-none outline-none focus:border-indigo-500 disabled:bg-slate-100"
                  placeholder={chatReady ? 'Type your question/response/answer...' : 'Click New Chat first...'}
                />

                <button
                  onClick={handleSend}
                  disabled={!chatReady || sending || !input.trim()}
                  className="w-12 rounded-xl bg-indigo-600 text-white flex items-center justify-center hover:bg-indigo-700 disabled:opacity-50"
                  title="Send"
                >
                  <Send size={18} />
                </button>
              </div>

              <p className="text-[10px] text-slate-400 mt-2 text-center">
                AI can make mistakes. Please verify important answers.
              </p>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

export default ChapterChat;
